import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    Avatar,
    AvatarGradientFallback,
    Button,
    Calendar,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Heading,
    Mono,
    Progress,
    Skeleton,
    StatusBadge,
    Text,
} from "@athena/ui";
import { AlertTriangle, CheckCircle2, ChevronDown, PencilIcon, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
    AssignmentDetailDocument,
    DeleteAssignmentDocument,
    LearnerProgressDocument,
    UpdateAssignmentDocument,
    UserQuizAttemptsDocument,
} from "../../graphql/assignments.generated";
import { deriveCourseStepProgress, orderByGlobalSortOrder } from "./progress";
import { cn } from "@athena/ui";
import { useTranslation } from "react-i18next";

interface UserAssignment {
    id: string;
    userId: string;
    name: string;
    email: string;
    initials: string;
    organization: string;
    dueDate: string;
    progress: string;
    status: "completed" | "in_progress" | "not_started";
}

interface AssignmentProgressProps {
    assignment: UserAssignment;
    /** Called with the new due date (ISO string) after a successful save. */
    onDueDateUpdated?: (dueDate: string) => void;
    /** Called after the assignment has been successfully deleted. */
    onDeleted?: () => void;
}

const statusVariantMap = {
    completed: "success",
    in_progress: "warning",
    not_started: "muted",
} as const;

const statusLabelMap = {
    completed: "Completed",
    in_progress: "In progress",
    not_started: "Not started",
} as const;

const questionTypeLabel: Record<string, string> = {
    SINGLE_SELECT: "Single choice",
    MULTIPLE_SELECT: "Multiple choice",
    TRUE_FALSE: "True / False",
    SHORT_ANSWER: "Short answer",
    LONG_ANSWER: "Open response",
};

type QuizAttempt = {
    id: string;
    quizId: string;
    passed: boolean;
    pointsEarned: number;
    totalPoints: number;
    submittedAt: string;
    questionResults: Array<{
        questionId: string;
        isCorrect: boolean;
        pointsEarned: number;
        submittedAnswer?: string | null;
    }>;
};

type QuizItem = {
    id: string;
    title: string;
    contentType: string;
    sortOrder: number;
    __typename?: string;
    passThreshold?: number;
    questions?: Array<{ id: string; questionType: string; questionText: string; points: number }>;
};

function QuizCard({
    item,
    quizAttempts,
    passed,
    bestPct,
    passThreshold,
}: {
    item: QuizItem;
    quizAttempts: QuizAttempt[];
    passed: boolean;
    bestPct: number | null;
    passThreshold: number;
}) {
    const { t } = useTranslation();

    const [
        open,
        setOpen,
    ] = useState<boolean>(false);
    const hasDetail = quizAttempts.length > 0 && item.__typename === "QuizContent" && (item.questions?.length ?? 0) > 0;
    const latest = quizAttempts[quizAttempts.length - 1];

    return (
        <div className="bg-muted/40 rounded-lg">
            <button
                type="button"
                className="flex w-full items-start justify-between gap-3 p-4 text-left"
                onClick={() => hasDetail && setOpen((o) => !o)}
                disabled={!hasDetail}
            >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    {hasDetail && (
                        <ChevronDown
                            className={cn(
                                "text-muted-foreground mt-0.5 h-4 w-4 shrink-0 transition-transform",
                                open && "rotate-180",
                            )}
                        />
                    )}
                    <div className="min-w-0">
                        <Text size="sm" weight="semibold">
                            {item.title}
                        </Text>
                        <Text muted size="xs">
                            {quizAttempts.length} attempt{quizAttempts.length !== 1 ? "s" : ""} · pass ≥ {passThreshold}
                            %
                        </Text>
                    </div>
                </div>
                {quizAttempts.length === 0 ? (
                    <StatusBadge dot size="ghost" variant="muted">
                        {t("admin.lms.notAttempted")}
                    </StatusBadge>
                ) : (
                    <div className="flex shrink-0 items-center gap-3">
                        {bestPct !== null && (
                            <div className="text-right">
                                <Text weight="semibold" className="text-lg leading-none">
                                    {bestPct}%
                                </Text>
                                <Text muted size="xs">
                                    {t("admin.lms.bestScore")}
                                </Text>
                            </div>
                        )}
                        <StatusBadge dot size="ghost" variant={passed ? "success" : "destructive"}>
                            {passed ? "Passed" : "Failed"}
                        </StatusBadge>
                    </div>
                )}
            </button>

            {open && hasDetail && latest && item.questions && (
                <div className="border-border border-t px-4 pt-3 pb-4">
                    <div className="mb-2 flex items-center justify-between">
                        <Text size="xs" muted>
                            Attempt #{quizAttempts.length} ·{" "}
                            {Math.round((latest.pointsEarned / (latest.totalPoints || 1)) * 100)}% ·{" "}
                            {latest.questionResults.filter((r) => r.isCorrect).length} of {item.questions.length}{" "}
                            correct
                        </Text>
                        <StatusBadge size="ghost" variant={latest.passed ? "success" : "destructive"}>
                            {latest.passed ? "Passed" : "Failed"}
                        </StatusBadge>
                    </div>
                    <div className="flex flex-col gap-2">
                        {item.questions.map((q, i) => {
                            const result = latest.questionResults.find((r) => r.questionId === q.id);
                            return (
                                <div key={q.id} className="border-border bg-background/60 rounded-md border p-3">
                                    <div className="flex items-start gap-2">
                                        {result?.isCorrect ? (
                                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500" />
                                        ) : (
                                            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                                        )}
                                        <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                                            <Text size="xs" weight="semibold">
                                                Q{i + 1} {q.questionText}
                                            </Text>
                                            <Mono size="xs" muted className="shrink-0">
                                                {questionTypeLabel[q.questionType] ?? q.questionType}
                                            </Mono>
                                        </div>
                                    </div>
                                    {result?.submittedAnswer != null && (
                                        <div className="mt-1 pl-5">
                                            <Text muted size="xs" className="tracking-wide uppercase">
                                                Answered
                                            </Text>
                                            <Text size="xs">{result.submittedAnswer}</Text>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export function AssignmentProgress({ assignment, onDueDateUpdated, onDeleted }: AssignmentProgressProps) {
    const { t } = useTranslation();

    const { data: detailData, loading: detailLoading } = useQuery(AssignmentDetailDocument, {
        variables: { id: assignment.id },
    });

    const [
        editDueDateOpen,
        setEditDueDateOpen,
    ] = useState(false);
    const [
        pendingDueDate,
        setPendingDueDate,
    ] = useState<Date | undefined>(undefined);
    const [
        updateAssignment,
        { loading: savingDueDate },
    ] = useMutation(UpdateAssignmentDocument);

    const [
        deleteOpen,
        setDeleteOpen,
    ] = useState(false);
    const [
        deleteAssignment,
        { loading: deleting },
    ] = useMutation(DeleteAssignmentDocument);

    const openEditDueDate = () => {
        setPendingDueDate(assignment.dueDate !== "—" ? new Date(assignment.dueDate) : undefined);
        setEditDueDateOpen(true);
    };

    const handleSaveDueDate = async () => {
        if (!pendingDueDate) return;
        try {
            const dueDate = pendingDueDate.toISOString();
            await updateAssignment({ variables: { id: assignment.id, input: { dueDate } } });
            setEditDueDateOpen(false);
            onDueDateUpdated?.(dueDate);
        } catch {
            toast.error("Couldn't update the due date. Please try again.");
        }
    };

    const handleDelete = async () => {
        try {
            await deleteAssignment({ variables: { id: assignment.id } });
            setDeleteOpen(false);
            onDeleted?.();
        } catch {
            toast.error("Couldn't remove this assignment. Please try again.");
        }
    };

    const { data: attemptsData, loading: attemptsLoading } = useQuery(UserQuizAttemptsDocument, {
        variables: { userId: assignment.userId },
    });

    const { data: progressData, loading: progressLoading } = useQuery(LearnerProgressDocument, {
        variables: { userId: assignment.userId },
    });

    const loading = detailLoading || attemptsLoading || progressLoading;

    const detail = detailData?.assignment;
    const course = detail?.course;
    const attempts = attemptsData?.userQuizAttempts ?? [];

    const attemptsByQuizId = attempts.reduce<Record<string, typeof attempts>>((acc, a) => {
        const list = acc[a.quizId] ?? [];
        list.push(a);
        acc[a.quizId] = list;
        return acc;
    }, {});

    const quizzes = (course?.content ?? [])
        .filter((item) => item.contentType === "QUIZ")
        .sort((a, b) => a.sortOrder - b.sortOrder);

    const progressByContentId = new Map(
        (progressData?.userProgress ?? []).map((p) => [
            p.contentId,
            p.status,
        ]),
    );
    const orderedContent = orderByGlobalSortOrder(course?.content ?? []);
    const { currentStepIndex, totalSteps } = deriveCourseStepProgress(orderedContent, progressByContentId);
    const progressPct = totalSteps > 0 ? Math.round(((currentStepIndex + 1) / totalSteps) * 100) : 0;

    const isOverdue = assignment.dueDate !== "—" && new Date(assignment.dueDate) < new Date();

    const failedQuizzes = quizzes.filter((q) => {
        const qa = attemptsByQuizId[q.id] ?? [];
        return qa.length > 0 && !qa.some((a) => a.passed);
    });

    const passedCount = quizzes.filter((q) => (attemptsByQuizId[q.id] ?? []).some((a) => a.passed)).length;

    return (
        <div className="flex h-full flex-col gap-5 overflow-y-auto p-6">
            <div className="flex flex-row justify-between">
                <div className="flex items-center gap-5">
                    <Avatar size="lg">
                        <AvatarGradientFallback>{assignment.initials}</AvatarGradientFallback>
                    </Avatar>
                    <div>
                        <Heading level={4}>{assignment.name}</Heading>
                        <Text muted size="sm">
                            {assignment.email}
                        </Text>
                        {assignment.organization && (
                            <Text muted size="xs">
                                {assignment.organization}
                            </Text>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Edit due date"
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditDueDate();
                        }}
                    >
                        <PencilIcon className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Delete assignment"
                        onClick={(e) => {
                            e.stopPropagation();
                            setDeleteOpen(true);
                        }}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            {/* Course card */}
            {loading ? (
                <Skeleton className="h-16 w-full rounded-lg" />
            ) : (
                course && (
                    <div className="bg-muted/40 flex items-center justify-between gap-4 rounded-lg p-4">
                        <div>
                            <Text size="sm" weight="semibold">
                                {course.title}
                            </Text>
                            {detail?.assignedAt && (
                                <Text muted size="xs">
                                    {t("admin.lms.enrolled")}{" "}
                                    {new Date(detail.assignedAt).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                    })}
                                </Text>
                            )}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                            <StatusBadge dot size="ghost" variant={statusVariantMap[assignment.status]}>
                                {statusLabelMap[assignment.status]}
                            </StatusBadge>
                            {assignment.dueDate !== "—" && (
                                <Text size="xs" className={isOverdue ? "text-destructive" : "text-muted-foreground"}>
                                    Due {assignment.dueDate}
                                    {isOverdue ? " · overdue" : ""}
                                </Text>
                            )}
                        </div>
                    </div>
                )
            )}

            {/* Step progress */}
            {!loading && totalSteps > 0 && (
                <div className="bg-muted/40 flex flex-col gap-3 rounded-lg p-4">
                    <div className="flex items-baseline justify-between">
                        <Text weight="semibold">
                            <span className="text-2xl">
                                {t("admin.lms.step")} {currentStepIndex + 1}
                            </span>
                            <span className="text-muted-foreground text-sm"> of {totalSteps}</span>
                        </Text>
                        <Text size="sm" weight="semibold">
                            {progressPct}%
                        </Text>
                    </div>
                    <Progress value={progressPct} className="h-2" />
                </div>
            )}

            {/* Failed quiz warning */}
            {!loading && failedQuizzes.length > 0 && (
                <div className="border-warning/30 bg-warning/10 flex items-start gap-3 rounded-lg border p-4">
                    <AlertTriangle className="text-warning mt-0.5 h-4 w-4 shrink-0" />
                    <Text size="sm" className="text-warning">
                        <span className="font-semibold">
                            {failedQuizzes.length} quiz{failedQuizzes.length !== 1 ? "zes" : ""}{" "}
                            {t("admin.lms.notYetPassed")}
                        </span>{" "}
                        {failedQuizzes.map((q) => `"${q.title}"`).join(", ")} {t("admin.lms.belowPassThreshold")}
                    </Text>
                </div>
            )}

            {/* Quiz results */}
            {loading ? (
                <div className="flex flex-col gap-3">
                    <Skeleton className="h-16 w-full rounded-lg" />
                    <Skeleton className="h-16 w-full rounded-lg" />
                    <Skeleton className="h-16 w-full rounded-lg" />
                </div>
            ) : (
                quizzes.length > 0 && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <Mono size="xs" muted className="tracking-widest uppercase">
                                Quiz responses &amp; grades
                            </Mono>
                            <Text muted size="xs">
                                {passedCount}/{quizzes.length} passed
                            </Text>
                        </div>
                        {quizzes.map((item) => {
                            const quizAttempts = [...(attemptsByQuizId[item.id] ?? [])].sort(
                                (a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime(),
                            );
                            const passed = quizAttempts.some((a) => a.passed);
                            const bestAttempt = quizAttempts.reduce<(typeof quizAttempts)[0] | null>((best, a) => {
                                if (!best) return a;
                                return a.pointsEarned > best.pointsEarned ? a : best;
                            }, null);
                            const bestPct = bestAttempt
                                ? Math.round((bestAttempt.pointsEarned / (bestAttempt.totalPoints || 1)) * 100)
                                : null;
                            const passThreshold = item.__typename === "QuizContent" ? (item.passThreshold ?? 80) : 80;

                            return (
                                <QuizCard
                                    key={item.id}
                                    item={item}
                                    quizAttempts={quizAttempts}
                                    passed={passed}
                                    bestPct={bestPct}
                                    passThreshold={passThreshold}
                                />
                            );
                        })}
                    </div>
                )
            )}

            <Dialog open={editDueDateOpen} onOpenChange={setEditDueDateOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t("common.actions.editVariable", { variable: "due date" })}</DialogTitle>
                        <DialogDescription>
                            {t("admin.lms.setNewDueDate")} {assignment.name || assignment.email}.
                        </DialogDescription>
                    </DialogHeader>
                    <Calendar
                        className="mx-auto rounded-lg border p-4 [--cell-size:2.25rem]"
                        mode="single"
                        selected={pendingDueDate}
                        onSelect={setPendingDueDate}
                        disabled={{ before: new Date() }}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDueDateOpen(false)}>
                            {t("common.actions.cancel")}
                        </Button>
                        <Button disabled={!pendingDueDate || savingDueDate} onClick={() => void handleSaveDueDate()}>
                            {savingDueDate ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t("common.actions.deleteVariable", { variable: "Assignment" })}</DialogTitle>
                        <DialogDescription>
                            {t("admin.lms.deleteAssignmentFor", { variable: assignment.name || assignment.email })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                            {t("common.actions.cancel")}
                        </Button>
                        <Button variant="destructive" disabled={deleting} onClick={() => void handleDelete()}>
                            {deleting ? "Deleting…" : "Delete assignment"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
